/**
 * Der Abschnittskopf des Kits — Überschrift, Unterzeile, rechts die Aktion.
 *
 * **Die Überschrift steht über der Karte, nicht in ihr.** Beides gibt es im
 * Design-System (`Card` kann einen Titel tragen), und beides nebeneinander sah
 * man der Gerätedetailseite an: Die Auskunftskarten trugen ihre Überschrift
 * außen, Konfiguration und Aktionen ihre innen — bei gleicher Schriftgröße
 * zwei verschiedene Abstände zur Karte darüber. Seit die Seite Reiter hat,
 * stehen mehrere Abschnitte untereinander in einer Fläche, und dort fällt es
 * auf.
 *
 * Die Datei ist entstanden, weil derselbe Kopf bereits **zweimal** im Ordner
 * stand — in `device-management.tsx` und in `device-detail.tsx`, mit einem
 * Unterschied im Aufbau, den niemand beabsichtigt hatte.
 */
import type { ReactNode } from "react";

export function SectionHeading({
  title,
  subtitle,
  action,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: ReactNode;
}) {
  return (
    /* `flex-end` setzt die Aktion auf die Grundlinie der Überschrift und nicht
       auf die Mitte des Blocks — im Kit steht sie dort.

       **`flex-wrap` seit HM-065/HM-066:** Eine Aktion neben einer langen
       Unterzeile quetschte den Text auf einem Telefon in eine Spalte von zwei
       Wörtern Breite, während der Umschalter daneben seinen Platz behielt. Sie
       rückt jetzt in die nächste Zeile, sobald es eng wird. */
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: "var(--sp-3)",
      }}
    >
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--text-strong)",
          }}
        >
          {title}
        </h2>
        {subtitle !== undefined && (
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
            {subtitle}
          </p>
        )}
      </div>
      {action !== undefined && <div style={{ marginLeft: "auto" }}>{action}</div>}
    </div>
  );
}

/** Kopf und Inhalt zusammen — die Form, in der das Kit jeden Abschnitt setzt. */
export function Section({
  title,
  subtitle,
  action,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading title={title} subtitle={subtitle} action={action} />
      {children}
    </section>
  );
}
